/**
 * The HTTP client.
 *
 * Every call goes through a typed Hono client (`hc`) built from the API's own route
 * types, so a path, a body or a response that disagrees with the API fails the
 * typecheck. Platform route types come from `@daka/api/rpc`; a module's from its
 * own `api/routes.ts`.
 *
 * Mirrors the API's contract: a failed response carries a code and message
 * *keys*, so this module reconstructs an `AppError` and never has to invent
 * English. Rendering happens where the translator lives — in a component.
 */
import { apiErrorBodySchema, AppError, ERROR_CODES, type ErrorCode, ErrorFactories, type ListQuery } from '@daka/core';
import type { Hono } from 'hono';
import { type ClientResponse, hc } from 'hono/client';

/** Inlined by Vite at build time (`VITE_API_URL`, always set by the Dockerfiles / compose). */
export const API_URL = import.meta.env.VITE_API_URL;

export async function toError(response: { readonly status: number; json(): Promise<unknown> }): Promise<AppError> {
   const body = apiErrorBodySchema.safeParse(await response.json().catch(() => null));
   if (!body.success) {
      // A body that is not the API's error envelope means something between us and the API failed.
      console.warn(`[http] ${response.status} response without an API error body`);
      return ErrorFactories.internal();
   }
   const { code, message, issues } = body.data;
   return new AppError(
      (Object.values(ERROR_CODES) as string[]).includes(code) ? (code as ErrorCode) : ERROR_CODES.INTERNAL_ERROR,
      message,
      issues
   );
}

/**
 * A typed client for the routes mounted at `path`.
 * `credentials: 'include'` is required: the session lives in an HttpOnly cookie.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- `hc`'s own constraint: any env, schema and base path.
export function apiClient<Routes extends Hono<any, any, any>>(path: string) {
   return hc<Routes>(`${API_URL}${path}`, { init: { credentials: 'include' } });
}

/** Every success body is `{ data }`: this returns `data`, or throws the API's `AppError`. */
export async function unwrap<Data>(request: Promise<ClientResponse<{ data: Data }, number, 'json'>>): Promise<Data> {
   const response = await request;
   if (!response.ok) throw await toError(response);
   return (await response.json()).data;
}

/** A list request's query: its page and order, plus any filters that are set. */
export function listQuery(query: ListQuery, filters: Readonly<Record<string, string | undefined>> = {}): Record<string, string> {
   const params: Record<string, string> = { page: String(query.page), pageSize: String(query.pageSize), dir: query.dir };
   if (query.sort) params.sort = query.sort;
   for (const [name, value] of Object.entries(filters)) if (value) params[name] = value;
   return params;
}
